import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, FileText, Download, AlertTriangle, Trash2, GraduationCap, Users } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function SigurimiReports() {
    const { user } = useAuth();
    const backHome = user?.role === "drejtoresha" ? "/drejtoresha" : user?.role === "super_admin" ? "/admin" : "/sigurimi";
    const [month, setMonth] = useState(thisMonth());
    const [agg, setAgg] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const [a, i] = await Promise.all([
                api.get("/sigurimi/aggregate", { params: { month } }),
                api.get("/sigurimi/incidents", { params: { month } }),
            ]);
            setAgg(a.data); setIncidents(i.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [month]);

    const getToken = () => localStorage.getItem("token");

    const downloadGeneral = async () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/sigurimi/report/pdf?month=${month}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) { alert("Gabim në shkarkim: " + r.status); return; }
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `sigurimi_${month}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    const downloadIndividual = async (subjectType, subjectId, subjectName) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/sigurimi/report/pdf/individual?subject_type=${subjectType}&subject_id=${subjectId}&month=${month}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) { alert("Gabim në shkarkim: " + r.status); return; }
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `sigurimi_${subjectName.replace(/\s+/g, "_")}_${month}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    const delIncident = async (id) => {
        if (!window.confirm("Fshi këtë incident?")) return;
        await api.delete(`/sigurimi/incident/${id}`);
        refresh();
    };

    return (
        <Layout>
            <Link to={backHome} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="sigrep-back">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Sigurimi</div>
                    <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Raportet</h1>
                    <p className="mt-1 text-slate-600 text-xs sm:text-sm">Dokumenti konfidencial — vetëm për Drejtoreshën.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" data-testid="sigrep-month" />
                    <Button onClick={downloadGeneral} className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="sigrep-download-general">
                        <Download className="w-4 h-4" /> PDF (i përgjithshëm)
                    </Button>
                </div>
            </div>

            {loading ? <div className="text-sm text-slate-500">Duke ngarkuar…</div> : !agg ? null : (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-5">
                        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="sigrep-stat-students">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Incidente nxënës</div>
                            <div className="text-3xl font-heading font-black text-oxford mt-1">{agg.totals.student}</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="sigrep-stat-teachers">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Incidente mësues</div>
                            <div className="text-3xl font-heading font-black text-oxford mt-1">{agg.totals.teacher}</div>
                        </div>
                    </div>

                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-oxford" /> Nxënësit ({agg.students.length})
                    </h2>
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
                        {agg.students.length === 0 ? <div className="p-6 text-sm text-slate-500">Asnjë incident.</div> : (
                            <div className="divide-y divide-slate-200">
                                {agg.students.map((s) => (
                                    <div key={s.subject_id} className="flex items-center gap-2 px-3 py-2.5" data-testid={`sigrep-student-${s.subject_id}`}>
                                        <div className="w-9 h-9 rounded-lg bg-oxford/10 text-oxford flex items-center justify-center font-bold text-xs shrink-0">{s.count}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 truncate">{s.subject_name}</div>
                                            <div className="text-[11px] text-slate-500">{s.class_name || "—"}</div>
                                        </div>
                                        <button onClick={() => downloadIndividual("student", s.subject_id, s.subject_name)}
                                            className="inline-flex items-center gap-1 text-xs text-oxford font-semibold hover:underline"
                                            data-testid={`sigrep-ind-student-${s.subject_id}`}>
                                            <FileText className="w-3.5 h-3.5" /> PDF individual
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5 text-oxford" /> Mësuesit ({agg.teachers.length})
                    </h2>
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-6">
                        {agg.teachers.length === 0 ? <div className="p-6 text-sm text-slate-500">Asnjë incident.</div> : (
                            <div className="divide-y divide-slate-200">
                                {agg.teachers.map((t) => (
                                    <div key={t.subject_id} className="flex items-center gap-2 px-3 py-2.5" data-testid={`sigrep-teacher-${t.subject_id}`}>
                                        <div className="w-9 h-9 rounded-lg bg-sage/15 text-sage flex items-center justify-center font-bold text-xs shrink-0">{t.count}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 truncate">{t.subject_name}</div>
                                        </div>
                                        <button onClick={() => downloadIndividual("teacher", t.subject_id, t.subject_name)}
                                            className="inline-flex items-center gap-1 text-xs text-oxford font-semibold hover:underline"
                                            data-testid={`sigrep-ind-teacher-${t.subject_id}`}>
                                            <FileText className="w-3.5 h-3.5" /> PDF individual
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-terracotta" /> Të gjitha regjistrimet këtë muaj ({incidents.length})
                    </h2>
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                        {incidents.length === 0 ? <div className="p-6 text-sm text-slate-500 text-center">Asnjë incident.</div> : (
                            <div className="divide-y divide-slate-200 max-h-[520px] overflow-auto">
                                {incidents.map((i) => (
                                    <div key={i.id} className="flex items-start gap-2 px-3 py-2.5">
                                        <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0 ${i.subject_type === "student" ? "bg-oxford/10 text-oxford" : "bg-sage/15 text-sage"}`}>
                                            {i.subject_type === "student" ? "Nxënës" : "Mësues"}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 text-sm truncate">{i.subject_name} {i.class_name ? <span className="text-slate-500 font-normal">• {i.class_name}</span> : null}</div>
                                            <div className="text-[11px] text-slate-600">{i.incident_type} • {i.date}</div>
                                            {i.note && <div className="text-[11px] text-slate-600 italic mt-0.5">"{i.note}"</div>}
                                        </div>
                                        {user?.role !== "drejtoresha" && (
                                            <button onClick={() => delIncident(i.id)} className="text-slate-400 hover:text-terracotta" data-testid={`sigrep-del-${i.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </Layout>
    );
}
